import type { HistoricalFundingRatePanelConfig } from '@src/types/LayoutConfig';
import { Box, getCurrencyColor } from '@talos/kyoko';
import type { SeriesLineOptions } from 'highcharts';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import {
  buildMissingMetricsErrorMessage,
  resolveExchangeDisplayName,
  SignalsDualAxisChart,
  SignalsModuleShell,
  useExchangeDisplayNameMap,
  useMissingMetrics,
  useSignalsAssetMap,
} from '../core';
import { abbreviateUsd, formatPercent } from '../core/utils';
import { getFallbackSeriesColor, getFundingExchangeColor } from './exchangeColor';
import { FundingRateLegend } from './FundingRateLegend';
import { buildFundingRateLegendItems, type FundingRateLegendSeriesInput } from './fundingRateLegendItems';
import type { FundingRateFrequency, FundingRateMAWindow, FundingRateTimeRange } from './HistoricalFundingRate.types';
import { FundingRateScope } from './HistoricalFundingRate.types';
import { HistoricalFundingRateToolbarLeft, HistoricalFundingRateToolbarRight } from './HistoricalFundingRateToolbar';
import { computeMovingAverage } from './movingAverage';
import {
  FUNDING_RATE_DEFAULT_FREQUENCY,
  FUNDING_RATE_DEFAULT_MA_WINDOW,
  FUNDING_RATE_DEFAULT_SCOPE,
  FUNDING_RATE_DEFAULT_TIME_RANGE,
  FUNDING_RATE_METRIC,
  FUNDING_RATE_METRIC_LABEL,
  FUNDING_RATE_PRICE_METRIC_LABEL,
  FUNDING_RATE_PRIMARY_AXIS_LABEL,
  FUNDING_RATE_SECONDARY_AXIS_LABEL,
  isFundingRateFrequencyRangeDisallowed,
} from './tokens';
import { useHistoricalFundingRateData } from './useHistoricalFundingRateData';

const ASSET_MAP_METRICS = [FUNDING_RATE_METRIC];

interface HistoricalFundingRateModuleProps {
  params: HistoricalFundingRatePanelConfig;
  onParamsChange: (partial: Partial<HistoricalFundingRatePanelConfig>) => void;
}

export function HistoricalFundingRateModule({ params, onParamsChange }: HistoricalFundingRateModuleProps) {
  const theme = useTheme();

  const scope = params.scope ?? FUNDING_RATE_DEFAULT_SCOPE;
  const rawFrequency = params.frequency ?? FUNDING_RATE_DEFAULT_FREQUENCY;
  const timeRange = params.timeRange ?? FUNDING_RATE_DEFAULT_TIME_RANGE;
  const maWindow = params.maWindow ?? FUNDING_RATE_DEFAULT_MA_WINDOW;

  // Coerce a persisted 1h + long-range combo (only reachable from older saved layouts) to daily
  // before the data hook runs, so the oversized query never fires on first render.
  const frequency: FundingRateFrequency = isFundingRateFrequencyRangeDisallowed(rawFrequency, timeRange)
    ? '1d'
    : rawFrequency;

  const {
    talosSymbols: availableTalosSymbols,
    cmAssetIdByTalosSymbol,
    talosSymbolByCmAssetId,
    catalogEntries,
    isLoading: isAssetsLoading,
    isError: isAssetsError,
  } = useSignalsAssetMap(ASSET_MAP_METRICS);

  // No frequency passed: FUNDING_RATE_METRIC can run hourly or daily (the user's frequency toggle),
  // so entitlement is checked at any cadence rather than the currently-selected one.
  const catalogMissingMetrics = useMissingMetrics(catalogEntries, ASSET_MAP_METRICS, isAssetsLoading);

  const { displayNameByExchangeId } = useExchangeDisplayNameMap();

  // Resolve the persisted CM asset ID back to a Talos symbol for the dropdown.
  const selectedTalosSymbol = params.cmAssetId != null ? talosSymbolByCmAssetId.get(params.cmAssetId) : undefined;

  const handleTalosSymbolChange = useCallback(
    (symbol: string | undefined) => {
      const cmAssetId = symbol != null ? cmAssetIdByTalosSymbol.get(symbol) : undefined;
      onParamsChange({ cmAssetId });
    },
    [cmAssetIdByTalosSymbol, onParamsChange]
  );

  const handleScopeChange = useCallback(
    (next: FundingRateScope) => {
      if (next !== scope) {
        onParamsChange({ scope: next });
      }
    },
    [scope, onParamsChange]
  );

  const handleFrequencyChange = useCallback(
    (next: FundingRateFrequency) => {
      if (next !== frequency) {
        onParamsChange({ frequency: next });
      }
    },
    [frequency, onParamsChange]
  );

  const handleTimeRangeChange = useCallback(
    (next: FundingRateTimeRange) => {
      if (next !== timeRange) {
        onParamsChange({ timeRange: next });
      }
    },
    [timeRange, onParamsChange]
  );

  const handleMAWindowChange = useCallback(
    (next: FundingRateMAWindow) => {
      if (next !== maWindow) {
        onParamsChange({ maWindow: next });
      }
    },
    [maWindow, onParamsChange]
  );

  const {
    fundingRateSeries,
    priceSeries,
    availableExchanges,
    selectedExchanges,
    fundingStartTime,
    isFetching,
    isError,
    isPriceForbidden,
    isTruncated,
  } = useHistoricalFundingRateData({
    cmAssetId: params.cmAssetId,
    assetDisplayName: selectedTalosSymbol,
    scope,
    frequency,
    timeRange,
    selectedExchanges: params.selectedExchanges,
  });

  const handleSelectedExchangesChange = useCallback(
    (exchanges: string[]) => onParamsChange({ selectedExchanges: exchanges }),
    [onParamsChange]
  );

  // Color each funding rate line by its dimension: per-exchange in By Exchange scope, and by the
  // asset's own brand color in asset-level scope. Both fall back to a deterministic non-blue palette
  // (never the price-line blue) so unknown venues/assets stay distinct — see exchangeColor.ts.
  const coloredFundingRateSeries = useMemo<SeriesLineOptions[]>(() => {
    // The hook already scopes the fetch to the selected exchanges, so no client-side filter here.
    // `s.name` is the bare exchange id in this scope (set by buildExchangeAssetFundingRateSeries);
    // it's resolved to a Talos display name later, in displayFundingRateSeries. `id` is pinned to
    // that raw id now, before the rename, so two exchanges that happen to resolve to the same
    // display name (e.g. a legacy id and its successor both mapping to the same venue) still get
    // distinct chart/legend identities instead of colliding.
    if (scope === FundingRateScope.ByExchange) {
      return fundingRateSeries.map(s => ({
        ...s,
        id: s.name,
        color: getFundingExchangeColor(s.name ?? '', theme),
      }));
    }
    const assetColor =
      getCurrencyColor(params.cmAssetId ?? '') ?? getFallbackSeriesColor(params.cmAssetId ?? '', theme);
    return fundingRateSeries.map(s => ({ ...s, color: assetColor }));
  }, [fundingRateSeries, scope, params.cmAssetId, theme]);

  // Compute MA overlay series (dashed, same color as source line). In By Exchange scope each
  // source line gets its own MA, so the name is prefixed with the exchange to keep it unique
  // (identical names would collide as legend keys and read as duplicate chips).
  const maSeries = useMemo<SeriesLineOptions[]>(() => {
    if (maWindow === 0) {
      return [];
    }
    // maWindow is in days; convert to points based on the active frequency.
    const pointsPerDay = frequency === '1h' ? 24 : 1;
    const maWindowPoints = maWindow * pointsPerDay;
    return coloredFundingRateSeries.flatMap(s => {
      const maData = computeMovingAverage(s.data as [number, number][], maWindowPoints);
      if (maData.length === 0) {
        return [];
      }
      const isByExchange = scope === FundingRateScope.ByExchange && s.id != null;
      const name = isByExchange
        ? `${resolveExchangeDisplayName(s.id as string, displayNameByExchangeId)} MA (${maWindow}D)`
        : `Moving Average (${maWindow}D)`;
      return [
        {
          type: 'line' as const,
          // Suffix the raw exchange id (not the resolved name) so two exchanges resolving to the
          // same display name still get distinct MA identities.
          id: isByExchange ? `${s.id}-ma` : undefined,
          name,
          data: maData,
          color: s.color,
          dashStyle: 'Dash' as const,
          lineWidth: 1,
        },
      ];
    });
  }, [coloredFundingRateSeries, maWindow, frequency, scope, displayNameByExchangeId]);

  // The price line is a reference overlay (its own axis), kept a consistent theme blue so it stays
  // visually distinct from the asset-colored funding rate line. Trim it to the funding start so the
  // chart, tooltip and legend all begin where funding data exists — price history reaches back much
  // further (e.g. All), and showing it would leave a long stretch with no funding line.
  const coloredPriceSeries = useMemo<SeriesLineOptions[]>(
    () =>
      priceSeries.map(s => {
        const points = s.data as [number, number][];
        const data = fundingStartTime == null ? points : points.filter(([time]) => time >= fundingStartTime);
        return { ...s, data, color: theme.colors.blue.lighten };
      }),
    [priceSeries, fundingStartTime, theme]
  );

  const displayFundingRateSeries = useMemo<SeriesLineOptions[]>(() => {
    if (scope !== FundingRateScope.ByExchange) {
      return coloredFundingRateSeries;
    }
    return coloredFundingRateSeries.map(s => ({
      ...s,
      name: resolveExchangeDisplayName(s.name ?? '', displayNameByExchangeId),
    }));
  }, [coloredFundingRateSeries, scope, displayNameByExchangeId]);

  const primarySeries = useMemo<SeriesLineOptions[]>(
    () => [...displayFundingRateSeries, ...maSeries],
    [displayFundingRateSeries, maSeries]
  );

  // Legend chips: funding rate + MA lines show their latest value (already %); the price line
  // shows its percent change over the window (its axis is USD, so an absolute value isn't comparable).
  const legendItems = useMemo(() => {
    const toInput = (
      series: SeriesLineOptions,
      marker: FundingRateLegendSeriesInput['marker'],
      valueMode: FundingRateLegendSeriesInput['valueMode']
    ): FundingRateLegendSeriesInput => ({
      // Prefer the stable id (raw exchange id, pinned before the Talos-name rename) so two
      // exchanges that resolve to the same display name don't collide as duplicate legend keys.
      id: series.id ?? series.name ?? '',
      label: series.name ?? '',
      data: (series.data ?? []) as [number, number][],
      color: (series.color as string) ?? '',
      marker,
      valueMode,
    });

    return buildFundingRateLegendItems([
      ...displayFundingRateSeries.map(s => toInput(s, 'solid', 'latest')),
      ...maSeries.map(s => toInput(s, 'dashed', 'latest')),
      ...coloredPriceSeries.map(s => toInput(s, 'solid', 'change')),
    ]);
  }, [displayFundingRateSeries, maSeries, coloredPriceSeries]);

  const isLoading = isFetching && primarySeries.length === 0 && coloredPriceSeries.length === 0;

  // Two independent entitlement checks feed the same message: the funding rate metric is gated via
  // the catalog (its query silently omits the metric rather than erroring), while the price overlay
  // metric (ReferenceRateUSD) always fires a direct, ungated query and reports its own 403 for the
  // same class of gap. Both surface as the same "key doesn't have access to X" message so the user
  // sees a single actionable list rather than one generic error hiding the other.
  const missingMetricLabels = useMemo(() => {
    const labels: string[] = [];
    // A failed catalog request also leaves catalogEntries empty, which looks identical to a
    // genuine entitlement gap to useMissingMetrics — only trust it once the catalog actually loaded.
    if (catalogMissingMetrics.length > 0 && !isAssetsError) {
      labels.push(FUNDING_RATE_METRIC_LABEL);
    }
    if (isPriceForbidden) {
      labels.push(FUNDING_RATE_PRICE_METRIC_LABEL);
    }
    return labels;
  }, [catalogMissingMetrics, isAssetsError, isPriceForbidden]);
  const hasMissingMetrics = missingMetricLabels.length > 0;

  // Truncated responses (pagination cap hit) drop the oldest data, so partial series would mislead.
  // Surface it as a hard error with an actionable message, consistent with the Comparison module.
  // hasMissingMetrics is handled separately, via isPermissionError below.
  const isErrorOrTruncated = isError || isTruncated || isAssetsError;
  let errorMessage: ReactNode = 'Failed to load funding rate data.';
  if (hasMissingMetrics) {
    errorMessage = buildMissingMetricsErrorMessage(missingMetricLabels);
  } else if (isTruncated) {
    errorMessage = 'Too much data for the selected range. Choose a shorter time range or switch to daily frequency.';
  }

  return (
    <SignalsModuleShell
      header={
        <HistoricalFundingRateToolbarLeft
          scope={scope}
          onScopeChange={handleScopeChange}
          availableTalosSymbols={availableTalosSymbols}
          selectedTalosSymbol={selectedTalosSymbol}
          onSelectedTalosSymbolChange={handleTalosSymbolChange}
          frequency={frequency}
          onFrequencyChange={handleFrequencyChange}
          timeRange={timeRange}
          isLoading={isAssetsLoading}
          availableExchanges={availableExchanges}
          selectedExchanges={selectedExchanges}
          onSelectedExchangesChange={handleSelectedExchangesChange}
          exchangeNameByExchangeId={displayNameByExchangeId}
        />
      }
      controls={
        <HistoricalFundingRateToolbarRight
          maWindow={maWindow}
          onMAWindowChange={handleMAWindowChange}
          timeRange={timeRange}
          onTimeRangeChange={handleTimeRangeChange}
          frequency={frequency}
          disabled={isFetching}
        />
      }
      isLoading={isLoading}
      isError={isErrorOrTruncated}
      errorMessage={errorMessage}
      isPermissionError={hasMissingMetrics}
    >
      <Box position="relative" flex="1" w="100%" h="100%">
        <FundingRateLegend items={legendItems} />
        <SignalsDualAxisChart
          primarySeries={primarySeries}
          secondarySeries={coloredPriceSeries}
          primaryAxisLabel={FUNDING_RATE_PRIMARY_AXIS_LABEL}
          secondaryAxisLabel={FUNDING_RATE_SECONDARY_AXIS_LABEL}
          formatPrimaryValue={formatPercent}
          formatSecondaryValue={abbreviateUsd}
        />
      </Box>
    </SignalsModuleShell>
  );
}
